import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Gizlilik Politikası (KVKK odaklı). Metnin standart hukuki iskeleti
 * korunur; şirkete özgü bilgiler ("Colossae Hotel") ve saklanan veriler
 * uygulama gereksinimlerine göre kalibre edilmiştir.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="privacy-page">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Colossae Personel</div>
          <h1 className="font-display text-3xl font-bold mt-1">Gizlilik Politikası</h1>
          <p className="text-sm text-muted-foreground mt-1">Son güncelleme: {new Date().toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>

        <Card><CardContent className="p-6 space-y-4 text-sm leading-relaxed">
          <section>
            <h2 className="font-display font-bold text-lg mb-1">1. Veri Sorumlusu</h2>
            <p>
              İşbu Gizlilik Politikası, <b>Colossae Hotel</b> ("Şirket") bünyesinde çalışan personel, müdürler, İnsan
              Kaynakları uzmanları ve yönetici kadronun kişisel verilerinin işlenmesine ilişkin usul ve esasları
              düzenler. Veri sorumlusu sıfatıyla Şirket, 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")
              ve ilgili ikincil mevzuata uygun olarak hareket eder.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">2. Toplanan Kişisel Veriler</h2>
            <p>Uygulama içerisinde yalnızca aşağıdaki veriler, çalışma ilişkisinin yürütülmesi amacıyla toplanır ve işlenir:</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Kimlik bilgileri (ad, soyad, T.C. kimlik numarası, doğum tarihi)</li>
              <li>İletişim bilgileri (e-posta, telefon, adres)</li>
              <li>Özlük bilgileri (departman, pozisyon, işe giriş/çıkış tarihi, izin kayıtları, ücret bilgisi)</li>
              <li>Finansal bilgiler (IBAN, banka adı, hesap sahibi adı, şube kodu)</li>
              <li>Görsel ve biyometrik nitelikte olmayan imza örneği</li>
              <li>Sisteme giriş bilgileri (IP adresi kısaltılmış, cihaz türü, tarayıcı, giriş zamanı)</li>
              <li>Personelin kendi rızasıyla yüklediği izin belgeleri (rapor, hastane belgesi vb.)</li>
              <li>Ödeme belgeleri (bordrolar)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">3. İşleme Amaçları</h2>
            <p>Kişisel veriler yalnızca aşağıdaki amaçlarla işlenir:</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>İş sözleşmesinin kurulması, ifası ve sona erdirilmesi</li>
              <li>Bordro düzenleme, maaş ödemesi ve avans hesaplama</li>
              <li>Yasal yükümlülüklerin yerine getirilmesi (SGK, vergi, iş sağlığı ve güvenliği)</li>
              <li>İzin taleplerinin yönetilmesi ve arşivlenmesi</li>
              <li>Şirket içi iletişim ve duyuru</li>
              <li>Bilgi güvenliği süreçlerinin işletilmesi (denetim, olay yönetimi)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">4. Verilerin Korunmasına Yönelik Teknik Tedbirler</h2>
            <p>Şirket, veri güvenliğini üst düzey teknik standartlarla sağlar:</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li><b>Uçtan uca TLS 1.2+ şifreleme</b> — Uygulama ile kullanıcı arasındaki tüm trafik HTTPS üzerinden aktarılır.</li>
              <li><b>Şifre saklama:</b> Kullanıcı şifreleri hiçbir zaman düz metin olarak tutulmaz; <b>bcrypt</b> algoritması ile tuzlanarak (salted hash) saklanır. Şifreler için kural denetimi (uzunluk, karmaşıklık, ardışık dizi engeli) uygulanır.</li>
              <li><b>Rol tabanlı erişim (RBAC):</b> Admin, İK, Genel Müdür, Muhasebe Müdürü, Departman Müdürü, Personel rolleri için ayrı yetki kümeleri tanımlanmıştır. Her kullanıcı yalnızca görevi için gerekli bilgileri görür.</li>
              <li><b>Maaş görünürlüğü kısıtı:</b> Maaş bilgisi yalnızca Admin/İK/GM ve yetkilendirilmiş departman müdürleri tarafından görülebilir; personel kendi maaşı hariç kimsenin maaşını göremez.</li>
              <li><b>Tanımlı cihaz kontrolü:</b> Her kullanıcının hesabına yalnızca doğrulanmış cihazlardan giriş yapılabilir. Bilinmeyen bir cihazdan giriş denendiğinde e-posta ile tek kullanımlık kod ve tanımlı cihazlara anlık onay bildirimi gönderilir.</li>
              <li><b>İki aşamalı doğrulama (2FA):</b> Kullanıcı isterse hesabında 2FA'yı zorunlu kılabilir.</li>
              <li><b>Denetim izleri (audit log):</b> Kritik işlemler (izin onayı, maaş değişikliği, kişisel veri güncelleme, silme) zaman damgalı olarak kayıt altına alınır.</li>
              <li><b>Kalıcı yedekleme:</b> Belgeler ve bordrolar geçici olmayan nesne depolama alanında (Emergent Object Storage) yedeklenir; günlük otomatik yedek çalıştırılır.</li>
              <li><b>IP maskeleme:</b> Kullanıcıya ait IP kayıtları arayüzde son iki oktet maskelenerek gösterilir.</li>
              <li><b>KVKK aydınlatma metni:</b> Kayıt sürecinde ayrıca sunulur; kullanıcı onayı olmadan hesap onaylanmaz.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">5. Yetkilendirme ve İdari Tedbirler</h2>
            <p>
              Personel kayıtları yalnızca Admin tarafından onaylanır. İK Sorumlusu, Admin'in verdiği yetki kapsamında
              işlem yapabilir. Şifreler kullanıcının kendi bilgisi dışında değiştirilemez; İK tarafından başlatılan
              her şifre değişikliği personelin kayıtlı imzasıyla onaylanmadan geçerli olmaz.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">6. Saklama Süresi</h2>
            <p>
              Kişisel veriler, iş ilişkisinin sona ermesinden itibaren mevzuatın öngördüğü süre boyunca (SGK, vergi
              ve iş hukuku kapsamında en az <b>10 yıl</b>) saklanır. Bu sürenin sonunda veriler silinir, imha edilir
              veya anonim hâle getirilir.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">7. Kişisel Verilere Erişim Hakkı</h2>
            <p>KVKK madde 11 uyarınca her personel aşağıdaki haklara sahiptir:</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Kişisel verilerinin işlenip işlenmediğini öğrenme</li>
              <li>İşleme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
              <li>Yurt içinde/dışında aktarıldığı üçüncü kişileri bilme</li>
              <li>Eksik/yanlış işlenen bilgilerin düzeltilmesini isteme</li>
              <li>Silme veya yok edilmesini isteme</li>
              <li>Otomatik sistemlerle analiz sonucu aleyhine bir sonuç çıkmasına itiraz etme</li>
              <li>Zarara uğraması hâlinde tazminat talep etme</li>
            </ul>
            <p className="mt-2">
              Bu hakların kullanımı için <b>ik@colossaehotel.com</b> adresine yazılı başvuru yapabilirsiniz.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-lg mb-1">8. Değişiklikler</h2>
            <p>
              Şirket, bu Gizlilik Politikası'nı zaman zaman güncelleyebilir. Önemli değişikliklerde tüm personele
              e-posta ve uygulama içi bildirim ile duyuru yapılır.
            </p>
          </section>

          <div className="pt-4 border-t border-border text-center">
            <Button asChild variant="outline"><Link to="/login">Girişe Dön</Link></Button>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}
